import { GLOBAL } from 'saddlebag-browser';

import CookieConnector from './cookieConnector';

const INIT_KEY = '__initTest__';
const INIT_VALUE = '__initValue';

// Guard against prototype pollution attacks
const isUnsafeKey = (key) =>
  key === '__proto__' || key === 'constructor' || key === 'prototype';

const isLocalStorageAvailable = () => {
  let localStorage;
  let sessionStorage;
  try {
    const $window = GLOBAL.getWindow();
    localStorage = $window.localStorage;
    sessionStorage = $window.sessionStorage;
    localStorage.setItem(INIT_KEY, INIT_VALUE);
    sessionStorage.setItem(INIT_KEY, INIT_VALUE);
  } catch (e) {
    return false;
  }
  // Clean up test keys to avoid polluting storage
  localStorage.removeItem(INIT_KEY);
  sessionStorage.removeItem(INIT_KEY);

  return true;
};

class LocalStorage {
  constructor(namespace) {
    this.namespace = namespace;
    this.browserStorage = null;
  }

  getBrowserStorage(fallbackToCookie, useSessionStorage) {
    const localStorageAvailable = isLocalStorageAvailable();
    const $window = GLOBAL.getWindow();
    if (localStorageAvailable) {
      this.browserStorage = useSessionStorage
        ? $window.sessionStorage
        : $window.localStorage;
    } else if (fallbackToCookie) {
      this.browserStorage = new CookieConnector(useSessionStorage);
    } else {
      this.browserStorage = null;
    }
    return this.browserStorage;
  }

  tryGetValue(key, fallbackToCookie, useSessionStorage) {
    const browserStorage = this.getBrowserStorage(
      fallbackToCookie,
      useSessionStorage,
    );
    if (!browserStorage) return null;

    let root = browserStorage.getItem(this.namespace);
    if (!root) return null;
    root = JSON.parse(root);
    if (key == null) return root;

    const parts = key.split('.');
    let current = root;
    for (let i = 0; i < parts.length; i += 1) {
      const path = parts[i];

      // Guard against prototype pollution
      if (isUnsafeKey(path)) {
        return null;
      }

      current = current[path];
      if (typeof current === 'undefined') return null;

      if (i + 1 === parts.length) {
        return current;
      }
    }
    return null;
  }

  trySetValue(
    key,
    value,
    fallbackToCookie,
    useSessionStorage,
    deleteValue = false,
  ) {
    const browserStorage = this.getBrowserStorage(
      fallbackToCookie,
      useSessionStorage,
    );
    if (!browserStorage) return null;
    let root;

    try {
      root = browserStorage.getItem(this.namespace);
      if (!root) {
        root = {};
      } else {
        root = JSON.parse(root);
      }
    } catch (err) {
      return false;
    }

    const parts = key.split('.');
    let current = root;

    for (let i = 0; i < parts.length; i += 1) {
      const path = parts[i];

      // Guard against prototype pollution
      if (isUnsafeKey(path)) {
        return false;
      }

      if (!(path in current)) {
        if (deleteValue) return false;
        current[path] = {};
      }

      if (i + 1 === parts.length) {
        if (deleteValue) {
          delete current[path];
        } else {
          current[path] = value;
        }
        try {
          browserStorage.setItem(this.namespace, JSON.stringify(root));
          return true;
        } catch (err) {
          return false;
        }
      }
      current = current[path];
    }
    return false;
  }

  tryDeleteValue(key, fallbackToCookie, useSessionStorage) {
    return this.trySetValue(
      key,
      null,
      fallbackToCookie,
      useSessionStorage,
      true,
    );
  }
}

export default LocalStorage;
